import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, HostListener, Injector, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { switchMap } from 'rxjs/operators';
import { Observable, Subscription, catchError, filter, interval, map, of, take } from 'rxjs';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { ProfileService } from '../../../common/service/data/profile.service';
import { UserAccountService } from '../../../common/service/user-account.service';
import { getMonthName } from '../../../core/helper/month';
import { Command, CommandBase, FilteredCommand, standardCommand } from '../../../core/model/command';
import { FilterDefinitionItem, FilterDefinitionItemLocalDate } from '../../../core/model/filter-definition-item';
import { LocalDate } from '../../../core/model/local-date';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteCommandHandler } from '../../../core/service/command/delete-command-handler.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { SidebarService } from '../../../core/service/sidebar.service';
import { EmailDeliveryStatus, EmailDeliveryStatusToString } from '../../../message/model/const/enums';
import { CustomPropertyVisibilityType, InvoicePeriod, InvoicePeriodToString, TenantCategory } from '../../../rent/model/const/enums';
import { invoiceCommand } from '../../command/unpaid-invoice';
import { GetInvoiceDeliveryMethodIcon, InvoiceDeliveryMethodToString, InvoiceType, InvoiceTypeToString } from '../../model/const/enum';
import { InvoiceCategory } from '../../model/invoice';
import { UnpaidInvoiceCommandHandlerResolver } from '../../service/command/unpaid-invoice-command-handler-resolver.service';
import { InvoiceService } from '../../service/data/invoice.service';
import { InvoiceListComponentBase } from '../base/invoice-list.base';
import { InvoiceListService } from './../../service/data/invoice-list.service';
import { CustomPropertyService } from '../../../rent/service/data/custom-property.service';
import { CustomPropertyHelper } from '../../../rent/helper/customproperties-helper';
import { ColumnVisibility } from '../../../core/model/column-definition';
import { CompanyService } from '../../../rent/service/data/company.service';
import { PropertyService } from '../../../rent/service/data/property.service';
import { ContactService } from '../../../rent/service/data/contact.service';
import { ArticleService } from '../../../rent/service/data/article.service';
import { BasicOwnerConfig } from '../../../common/model/basic-owner-config';
import { LanguageService } from '../../../core/service/language.service';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { ProtectedIdentityAccess } from '../../../rent/model/protected-identity';
import { ProtectedIdentityService } from '../../../rent/service/data/protected-identity.service';
import { ProtectedIdentityBankIdDialogComponent } from '../../../rent/component/protected-identity-bankid-dialog/protected-identity-bankid-dialog.component';
import { filterStateSignature, withIgnoreSaveOnLoad } from '../../../core/service/list-query-params';
import { parseInvoiceDrilldownQuery } from '../../helper/invoice-drilldown-filter';

@Component({
  selector: 'app-all-invoices',
  templateUrl: './invoice-list.component.html',
  styleUrls: ['./../../../core/component/paged-object-manager/paged-object-manager.scss', './invoice-list.component.scss'],
  providers: [DeleteCommandHandler, UnpaidInvoiceCommandHandlerResolver, InvoiceListService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class InvoiceListComponent extends InvoiceListComponentBase implements OnInit, OnDestroy {
  @ViewChild('createAmortizationPlanSidebar', { static: false }) createAmortizationPlanSidebar: TemplateRef<any>;

  amortizationPlanInvoiceIds: string[] = [];
  hasProtectedIdentityAccessSession = false;
  secondsRemaining = 0;

  private accessExpiresAt = 0;
  private accessTimerSubscription?: Subscription;
  private hasProtectedIdentityFeature = false;
  private isCheckingAccess = false;
  private externalQueryActive = false;
  private effectiveRecipientOnly = false;
  private externalQuerySeedSignature?: string;

  constructor(
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _dataService: InvoiceService,
    private readonly _profileService: ProfileService,
    private readonly _languageService: LanguageService,
    private readonly accountService: UserAccountService,
    dispatcher: CommandDispatcher,
    factory: ColumnDefinitionFactory,
    handlerResolver: UnpaidInvoiceCommandHandlerResolver,
    messageService: MessageService,
    navigationService: NavigationService,
    private readonly _sidebarService: SidebarService,
    private readonly _customPropertyService: CustomPropertyService,
    private readonly _ch: CustomPropertyHelper,
    private readonly _invoiceListService: InvoiceListService,
    private readonly _companyService: CompanyService,
    private readonly _propertyService: PropertyService,
    private readonly _contactService: ContactService,
    private readonly _articleService: ArticleService,
    private readonly actionWindowService: ActionWindowService,
    private readonly injector: Injector,
    private readonly protectedIdentityService: ProtectedIdentityService
  ) {
    super(factory, accountService, _dataService, dispatcher, handlerResolver, messageService, navigationService, _invoiceListService);

    this.AddSubscription(
      accountService.current.subscribe({
        next: account => {
          this.hasProtectedIdentityFeature = account.hasProtectedIdentityAccess();
          if (!this.hasProtectedIdentityFeature) this.removeProtectedIdentityFilter();
          this.allowNew = account.hasAccess(ModuleNames.Invoicing, Features.ManageInvoices);
          if (this.allowNew) {
            this.addContextMenuItemDefinition('INVOICE.COMMAND.CREATE_AMORTIZATION_PLAN', invoiceCommand.createAmortizationPlan);
            this.addMenuItemDefinition('INVOICE.COMMAND.CREATE_AMORTIZATION_PLAN', invoiceCommand.createAmortizationPlan);
          }
        },
      })
    );
    this.preferencesKey = 'invoice.list';
    this.columnDefinition = this._getColumnDefinitionGroup();
    this.showExcelExportButton = true;
    this.excelFileName = 'Fakturor';
    this.excelContext = 'CustomerLedgerInvoices';
    this.createExcelColumnDefinitions(factory);
  }

  private createExcelColumnDefinitions(factory: ColumnDefinitionFactory) {
    this.excelColumnDefinition = [...this.columnDefinition];
    this.excelColumnDefinition.push(
      factory.create('RENTAL_OBJECT_LIST.RENTAL_ID_COLUMN', 'rentalId', ColumnVisibility.optional),
      factory.create('LANDLORD.ORGANIZATION_NUMBER', 'organizationNumber', ColumnVisibility.optional),
      factory.create('COMMON.INVOICE_ADDRESS', 'invoiceAddress', ColumnVisibility.optional),
      factory.create('INVOICE.PREVIEW.ROUNDING', 'rounding', ColumnVisibility.optional),
      factory.create('COMMON.CREDITOR', 'creditor', ColumnVisibility.optional),
      factory.create('INVOICE.DEBTOR_EMAIL', 'email', ColumnVisibility.optional),
      factory.create('LEASE_AGREEMENT.LEASE_AGREEMENT_NUMBER', 'leaseAgreementNumber', ColumnVisibility.optional),
      factory.create('RENTAL_OBJECT_LIST.RENTAL_OBJECT_TYPE_COLUMN', 'rentalObjectType', ColumnVisibility.optional),
      factory.create('INVOICE.DEBTOR_ONE', 'debtor1', ColumnVisibility.optional),
      factory.create('INVOICE.DEBTOR_TWO', 'debtor2', ColumnVisibility.optional),
      factory.create('INVOICE.RECIPIENT_IDENTITY', 'invoiceRecipientIdentity', ColumnVisibility.optional),
      factory.create('INVOICE.RECIPIENT_PHONE', 'invoiceRecipientPhone', ColumnVisibility.optional),
      factory.create('INVOICE.REGULATED_TO', 'regulatedTo', ColumnVisibility.optional),
      factory.create('INVOICE.RECONCILIATION_UNPAID', 'reconciliationUnpaid', ColumnVisibility.optional),
      factory.create('JOINT_DEBITING_LIST.RENTAL_OBJECT_ADDRESS_COLUMN', 'rentalObjectAddress', ColumnVisibility.optional),
      factory.create('INVOICE.DEBTOR_ONE_IDENTITY', 'debtor1Identity', ColumnVisibility.optional),
      factory.create('INVOICE.DEBTOR_ONE_PHONE', 'debtor1Phone', ColumnVisibility.optional),
      factory.create('INVOICE.DEBTOR_TWO_IDENTITY', 'debtor2Identity', ColumnVisibility.optional),
      factory.create('INVOICE.DEBTOR_TWO_PHONE', 'debtor2Phone', ColumnVisibility.optional),
      factory.create('COMMON.GUARANTOR', 'guarantor', ColumnVisibility.optional),
      factory.create('INVOICE.GUARANTOR_IDENTITY', 'guarantorIdentity', ColumnVisibility.optional),
      factory.create('INVOICE.GUARANTOR_PHONE', 'guarantorPhone', ColumnVisibility.optional)
    );
  }

  private _category: InvoiceCategory;
  private _emailDeliveryStatus: EmailDeliveryStatus;
  private _recipientContactId?: string;

  override canExecute(command: CommandBase): boolean {
    if (command.id === invoiceCommand.createAmortizationPlan) {
      if (command instanceof Command) {
        return !command.contextId || command.contextId === this.contextId;
      }

      if (command instanceof FilteredCommand) {
        return command.totalAffectedItemsControlValue > 0;
      }

      return false;
    }

    return super.canExecute(command);
  }

  override execute(command: CommandBase): void {
    if (command.id === invoiceCommand.createAmortizationPlan) {
      if (this.isBlockedExternalAccountsReceivableCommand(command)) {
        this.messageService.showMessage('INVOICE.EXTERNAL_ACCOUNTS_RECEIVABLE.BLOCKED');
        return;
      }

      if (command instanceof Command && command.context?.id) {
        this.openCreateAmortizationPlanSidebar([command.context.id]);
      } else if (command instanceof FilteredCommand) {
        if (command.includedIds?.length > 0) {
          this.openCreateAmortizationPlanSidebar(command.includedIds);
        } else {
          this.messageService.showMessage('AMORTIZATION_PLAN.CREATE.FILTERED_SELECTION_NOT_SUPPORTED');
        }
      }

      return;
    }

    super.execute(command);
  }

  override filterInternal(filterState: any): void {
    this._ch.applyCustomPropertyFilters(filterState);
  }

  ngOnInit() {
    const drilldown = parseInvoiceDrilldownQuery(this._activatedRoute.snapshot.queryParamMap);
    this._category = drilldown.category;
    this._emailDeliveryStatus = drilldown.emailDeliveryStatus;
    this._recipientContactId = drilldown.recipientContactId;
    this.externalQueryActive = drilldown.isExternal;
    this.effectiveRecipientOnly = drilldown.effectiveRecipientOnly;
    const externalQuery = this.externalQueryActive;

    this.AddSubscription(
      this._customPropertyService
        .getList()
        .pipe(
          switchMap(customProperties =>
            this._invoiceListService.getFilterDefinition().pipe(
              switchMap(definition =>
                (this.hasProtectedIdentityFeature
                  ? this.protectedIdentityService.getAccess(false)
                  : of({ hasAccess: false, sessionSecondsRemaining: 0 })
                ).pipe(
                  catchError(() => of({ hasAccess: false, sessionSecondsRemaining: 0 })),
                  map(access => ({ customProperties, definition, access }))
                )
              )
            )
          )
        )
        .subscribe(({ customProperties, definition, access }) => {
          this.defaultFilters = definition.defaultFields;
          const invPeriodsFrom = definition.invoicePeriods.map(x => {
            return {
              caption: `${x.month ? this._languageService.instant(getMonthName(x.month)) : ''} ${x.year ? x.year + ' ' : ''}`,
              value: x.toId(),
            };
          });
          invPeriodsFrom.splice(0, 0, { caption: 'COMMON.NOT_SPECIFIED', value: '' });

          const invPeriodsTo = definition.invoicePeriods.map(x => ({
            caption: `${x.month ? this._languageService.instant(getMonthName(x.month)) : ''} ${x.year ? x.year + ' ' : ''}`,
            value: x.toId(),
          }));
          invPeriodsTo.splice(0, 0, { caption: 'COMMON.NOT_SPECIFIED', value: '' });

          const previousMonth = LocalDate.today();
          previousMonth.addMonths(-1);
          const openAtPeriod = definition.invoicePeriods
            .find(period => period.year === previousMonth.year && period.month === previousMonth.month)
            ?.toId();

          const periodFromFilter = new FilterDefinitionItem(
            'INVOICE.PERIOD_FROM',
            'periodFrom',
            externalQuery ? '' : definition.periodFrom,
            invPeriodsFrom,
            definition.selectedFields.includes('periodFrom')
          );
          periodFromFilter.openAtValue = openAtPeriod;
          periodFromFilter.visibleOptionCount = 12;

          const periodToFilter = new FilterDefinitionItem(
            'INVOICE.PERIOD_TO',
            'periodTo',
            externalQuery ? '' : definition.periodTo,
            invPeriodsTo,
            definition.selectedFields.includes('periodTo')
          );
          periodToFilter.openAtValue = openAtPeriod;
          periodToFilter.visibleOptionCount = 12;

          const yesNo = [
            { caption: 'YES_TEXT', value: 1 },
            { caption: 'NO_TEXT', value: 2 },
          ];
          yesNo.splice(0, 0, { caption: 'COMMON.NOT_SPECIFIED', value: 0 });

          const selectedCompanies = definition.company
            ? Array.isArray(definition.company)
              ? definition.company
              : [definition.company]
            : [];

          const selectedArticles = definition.articles
            ? Array.isArray(definition.articles)
              ? definition.articles
              : [definition.articles]
            : [];

          let category = InvoiceCategory.Any;
          if (this._category !== undefined) {
            category = this._category;
          } else if (!externalQuery && definition.category) category = definition.category;

          let emailStatus = 0;
          if (this._emailDeliveryStatus !== undefined) {
            emailStatus = this._emailDeliveryStatus;
          } else if (!externalQuery && definition.emlDeliveryStatusKind) emailStatus = definition.emlDeliveryStatusKind;

          const deliverySelection = [{ caption: 'INVOICE.CATEGORY.ANY', value: 0, icon: undefined }];

          for (const method of definition.invoiceDeliveryMethods) {
            deliverySelection.push({
              caption: InvoiceDeliveryMethodToString(method),
              value: method,
              icon: GetInvoiceDeliveryMethodIcon(method),
            });
          }

          const recipientCategorySelection = [
            { caption: 'INVOICE.CATEGORY.ANY', value: null },
            { caption: 'CONST.TENANT_CATEGORY_PHYSICAL_PERSON', value: TenantCategory.PhysicalPerson },
            { caption: 'CONST.TENANT_CATEGORY_COMPANY', value: TenantCategory.Company },
          ];

          const invoiceTypeSelection = [
            { caption: 'INVOICE.CATEGORY.ANY', value: null },
            { caption: InvoiceTypeToString(InvoiceType.Credit), value: InvoiceType.Credit },
            { caption: InvoiceTypeToString(InvoiceType.General), value: InvoiceType.General },
            { caption: InvoiceTypeToString(InvoiceType.Rent), value: InvoiceType.Rent },
          ];

          const invoiceNumberFilter = new FilterDefinitionItem(
            'INVOICE.INVOICE_NUMBER',
            'invoiceNumber',
            externalQuery ? '' : definition.invoiceNumber,
            null,
            definition.selectedFields.includes('invoiceNumber')
          );
          invoiceNumberFilter.placeholderText = 'INVOICE.INVOICE_NUMBER';

          this.filterDefinition = [
            new FilterDefinitionItem(
              'INVOICE.QUERY.CATEGORY',
              'category',
              category,
              [
                { caption: 'INVOICE.CATEGORY.ANY', value: InvoiceCategory.Any },
                { caption: 'INVOICE.CATEGORY.NOT_ATTESTED', value: InvoiceCategory.NotAttested },
                { caption: 'INVOICE.CATEGORY.ATTESTED', value: InvoiceCategory.Attested },
                { caption: 'INVOICE.CATEGORY.OVERDUE', value: InvoiceCategory.Overdue },
                { caption: 'INVOICE.CATEGORY.PARTIALLY_PAID', value: InvoiceCategory.PartiallyPaid },
                { caption: 'INVOICE.CATEGORY.UNPAID', value: InvoiceCategory.Unpaid },
                { caption: 'INVOICE.CATEGORY.FULLY_PAID', value: InvoiceCategory.FullyPaid },
              ],
              this._category !== undefined || definition.selectedFields.includes('category')
            ),
            new FilterDefinitionItem(
              'COMPANY.LIST_HEADER',
              'company',
              externalQuery ? [] : selectedCompanies,
              this._companyService,
              definition.selectedFields.includes('company'),
              true
            ),
            new FilterDefinitionItem(
              'ARTICLE.EDIT_HEADER',
              'articles',
              externalQuery ? [] : selectedArticles,
              this._articleService,
              definition.selectedFields.includes('articles'),
              true
            ),
            invoiceNumberFilter,
            new FilterDefinitionItem(
              'INVOICE.OCRNUMBER',
              'ocr',
              externalQuery ? '' : definition.ocr,
              null,
              definition.selectedFields.includes('ocr')
            ),
            new FilterDefinitionItem(
              'TENANT.EDIT_HEADER',
              'recipientContactId',
              this._recipientContactId ?? (externalQuery ? '' : definition.recipientContactId),
              this._contactService,
              !!this._recipientContactId || definition.selectedFields.includes('recipientContactId')
            ),
            periodFromFilter,
            periodToFilter,
            new FilterDefinitionItemLocalDate(
              'INVOICE.CREATED',
              'invoiceDate',
              externalQuery ? null : definition.invoiceDate,
              definition.selectedFields.includes('invoiceDate')
            ),
            new FilterDefinitionItemLocalDate(
              'INVOICE.DUE_DATE',
              'dueDate',
              externalQuery ? null : definition.dueDate,
              definition.selectedFields.includes('dueDate')
            ),
            new FilterDefinitionItem(
              'INVOICE.DELIVERY_METHOD',
              'invDeliveryMethod',
              externalQuery ? 0 : definition.invDeliveryMethod,
              deliverySelection,
              definition.selectedFields.includes('invDeliveryMethod')
            ),
            new FilterDefinitionItem(
              'COMMON.EMAIL_DELIVERY_STATUS_EMAIL',
              'emlDeliveryStatusKind',
              emailStatus,
              [
                { caption: 'INVOICE.CATEGORY.ANY', value: 0 },
                { caption: EmailDeliveryStatusToString(EmailDeliveryStatus.Processed), value: EmailDeliveryStatus.Processed },
                { caption: EmailDeliveryStatusToString(EmailDeliveryStatus.Dropped), value: EmailDeliveryStatus.Dropped },
                { caption: EmailDeliveryStatusToString(EmailDeliveryStatus.Deferred), value: EmailDeliveryStatus.Deferred },
                { caption: EmailDeliveryStatusToString(EmailDeliveryStatus.Delivered), value: EmailDeliveryStatus.Delivered },
                { caption: EmailDeliveryStatusToString(EmailDeliveryStatus.Open), value: EmailDeliveryStatus.Open },
                { caption: EmailDeliveryStatusToString(EmailDeliveryStatus.SpamReport), value: EmailDeliveryStatus.SpamReport },
                {
                  caption: EmailDeliveryStatusToString(EmailDeliveryStatus.NotDeliveredLastWeek),
                  value: EmailDeliveryStatus.NotDeliveredLastWeek,
                },
              ],
              this._emailDeliveryStatus !== undefined || definition.selectedFields.includes('emlDeliveryStatusKind')
            ),
            new FilterDefinitionItem(
              'LEASE_AGREEMENT.INVOICE_PERIOD',
              'invPeriodMonths',
              externalQuery ? 0 : definition.invPeriodMonths,
              [
                { caption: 'INVOICE.CATEGORY.ANY', value: 0 },
                { caption: InvoicePeriodToString(InvoicePeriod.Month), value: InvoicePeriod.Month },
                { caption: InvoicePeriodToString(InvoicePeriod.TwoMonths), value: InvoicePeriod.TwoMonths },
                { caption: InvoicePeriodToString(InvoicePeriod.Quarter), value: InvoicePeriod.Quarter },
                { caption: InvoicePeriodToString(InvoicePeriod.Tertrial), value: InvoicePeriod.Tertrial },
                { caption: InvoicePeriodToString(InvoicePeriod.HalfYear), value: InvoicePeriod.HalfYear },
                { caption: InvoicePeriodToString(InvoicePeriod.Year), value: InvoicePeriod.Year },
              ],
              definition.selectedFields.includes('invPeriodMonths')
            ),

            new FilterDefinitionItem(
              'RENTAL_OBJECT_FORM.PROPERTY_LABEL',
              'property',
              externalQuery ? '' : definition.property,
              this._propertyService,
              definition.selectedFields.includes('property')
            ),
            new FilterDefinitionItem(
              'INVOICE.DIRECT_DEBIT_INVOICE',
              'directDebit',
              externalQuery ? 0 : definition.directDebit,
              yesNo,
              definition.selectedFields.includes('directDebit')
            ),
            new FilterDefinitionItem(
              'INVOICE.VAT_INVOICE',
              'vatInvoice',
              externalQuery ? 0 : definition.vatInvoice,
              yesNo,
              definition.selectedFields.includes('vatInvoice')
            ),
            new FilterDefinitionItem(
              'INVOICE.SENT_FOR_DEBT_COLLECTION',
              'sentToDebtCollection',
              externalQuery ? null : definition.sentToDebtCollection,
              [
                { caption: 'INVOICE.CATEGORY.ANY', value: null },
                { caption: 'YES_TEXT', value: true },
                { caption: 'NO_TEXT', value: false },
              ],
              definition.selectedFields.includes('sentToDebtCollection')
            ),
            new FilterDefinitionItem(
              'LEASE_AGREEMENT.LABELING',
              'labeling',
              externalQuery ? '' : definition.labeling,
              null,
              definition.selectedFields.includes('labeling')
            ),
            new FilterDefinitionItem(
              'RENTAL_OBJECT_FORM.RENTAL_OBJECT_NUMBER_LABEL',
              'rentalId',
              externalQuery ? '' : definition.rentalId,
              null,
              definition.selectedFields.includes('rentalId')
            ),
            new FilterDefinitionItem(
              'INVOICE.DELIVERY_METHOD_EXECUTED',
              'invDeliveryMethodExecuted',
              externalQuery ? 0 : definition.invDeliveryMethodExecuted,
              deliverySelection,
              definition.selectedFields.includes('invDeliveryMethodExecuted')
            ),
            new FilterDefinitionItem(
              'INVOICE.RECIPIENT_CATEGORY',
              'recipientCategory',
              externalQuery ? null : definition.recipientCategory,
              recipientCategorySelection,
              definition.selectedFields.includes('recipientCategory')
            ),
            new FilterDefinitionItem(
              'INVOICE.TYPE',
              'invoiceType',
              externalQuery ? null : definition.invoiceType,
              invoiceTypeSelection,
              definition.selectedFields.includes('invoiceType')
            ),
          ];

          if (this.hasProtectedIdentityFeature) {
            this.filterDefinition.push(
              new FilterDefinitionItem(
                'PROTECTED_IDENTITY.FILTER',
                'protectedIdentityStatus',
                !externalQuery && access.hasAccess ? definition.protectedIdentityStatus : null,
                [
                  { caption: 'ALL_TEXT', value: '' },
                  { caption: 'YES_TEXT', value: true },
                  { caption: 'NO_TEXT', value: false },
                ],
                definition.selectedFields.includes('protectedIdentityStatus')
              )
            );
          }

          const customPropertiesFilters = this._ch.generateCustomPropertyFilters(
            customProperties,
            definition,
            CustomPropertyVisibilityType.Company
          );
          this._ch.createCustomPropertyColumnDefinition(customProperties, this.columnDefinition, CustomPropertyVisibilityType.Company);
          this.filterDefinition = [...this.filterDefinition, ...customPropertiesFilters];
          this.externalQuerySeedSignature = this.getExternalFilterSignature();
          if (this.hasProtectedIdentityFeature && access.hasAccess) {
            this.startAccessTimer(access.sessionSecondsRemaining);
          }
        })
    );

    if (this.allowNew) {
      this.addEditDeleteCommands();
    } else {
      this.addContextMenuItemDefinition('EDIT_BTN', standardCommand.edit);
    }
  }

  get formattedAccessTime(): string {
    const minutes = Math.floor(this.secondsRemaining / 60);
    const seconds = this.secondsRemaining % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  override onFilterChange(filterState: any): void {
    if (this.externalQueryActive && this.externalQuerySeedSignature && this.externalQuerySeedSignature !== filterStateSignature(filterState)) {
      this.externalQueryActive = false;
      this.effectiveRecipientOnly = false;
    }

    const normalizedFilter = {
      ...filterState,
      protectedIdentityStatus: this.toProtectedIdentityFilterValue(filterState.protectedIdentityStatus),
      ignoreSaveOnLoad: this.externalQueryActive,
      effectiveRecipientOnly: this.effectiveRecipientOnly,
    };

    if (normalizedFilter.protectedIdentityStatus === null) {
      super.onFilterChange(normalizedFilter);
      return;
    }

    let accessGranted = false;
    this.AddSubscription(
      this.requestProtectedIdentityAccess()
        .pipe(take(1), filter(access => access?.hasAccess === true))
        .subscribe({
          next: access => {
            accessGranted = true;
            this.startAccessTimer(access.sessionSecondsRemaining);
            super.onFilterChange(normalizedFilter);
          },
          complete: () => {
            if (!accessGranted) this.resetProtectedIdentityFilter();
          },
          error: () => this.resetProtectedIdentityFilter(),
        })
    );
  }

  protected override preparePageFilter(filter: any): any {
    return {
      ...withIgnoreSaveOnLoad(filter, this.externalQueryActive),
      effectiveRecipientOnly: this.effectiveRecipientOnly,
    };
  }

  private getExternalFilterSignature(): string {
    const initial = Object.fromEntries(this.filterDefinition.filter(item => item.selected).map(item => [item.name, item.value]));
    initial['selectedFields'] = this.filterDefinition.filter(item => item.selected).map(item => item.name);
    return filterStateSignature(initial);
  }

  extendProtectedIdentityAccess(): void {
    if (!this.hasProtectedIdentityAccessSession || this.secondsRemaining === 0) return;

    this.AddSubscription(
      this.protectedIdentityService.extendAccess().subscribe({
        next: access => this.startAccessTimer(access.sessionSecondsRemaining),
        error: () => this.clearAccessSession(),
      })
    );
  }

  endProtectedIdentityAccess(): void {
    this.AddSubscription(
      this.protectedIdentityService.endAccess().subscribe({
        next: () => this.clearAccessSession(),
        error: () => this.clearAccessSession(),
      })
    );
  }

  @HostListener('window:focus')
  onWindowFocus(): void {
    this.refreshAccessState();
  }

  @HostListener('document:visibilitychange')
  onVisibilityChange(): void {
    if (document.visibilityState === 'visible') this.refreshAccessState();
  }

  override ngOnDestroy(): void {
    this.stopAccessTimer();
    super.ngOnDestroy();
  }

  private requestProtectedIdentityAccess(): Observable<ProtectedIdentityAccess> {
    return this.protectedIdentityService.getAccess().pipe(
      switchMap(access =>
        access.hasAccess
          ? of(access)
          : this.actionWindowService.open(ProtectedIdentityBankIdDialogComponent, this.injector)
      )
    );
  }

  private startAccessTimer(seconds: number): void {
    this.stopAccessTimer();
    this.hasProtectedIdentityAccessSession = seconds > 0;
    this.accessExpiresAt = Date.now() + Math.max(0, seconds) * 1000;
    this.updateAccessTimer();
    if (!this.hasProtectedIdentityAccessSession) return;

    this.accessTimerSubscription = interval(1000).subscribe(() => this.updateAccessTimer());
  }

  private updateAccessTimer(): void {
    this.secondsRemaining = Math.max(0, Math.ceil((this.accessExpiresAt - Date.now()) / 1000));
    if (this.secondsRemaining > 0) return;

    this.clearAccessSession();
  }

  private clearAccessSession(): void {
    this.stopAccessTimer();
    this.hasProtectedIdentityAccessSession = false;
    this.resetProtectedIdentityFilter();
  }

  private refreshAccessState(): void {
    if (!this.hasProtectedIdentityAccessSession || this.isCheckingAccess) return;

    this.isCheckingAccess = true;
    this.AddSubscription(
      this.protectedIdentityService.getAccess(false).subscribe({
        next: access => {
          this.isCheckingAccess = false;
          if (access.hasAccess) {
            this.startAccessTimer(access.sessionSecondsRemaining);
            return;
          }
          this.clearAccessSession();
        },
        error: () => {
          this.isCheckingAccess = false;
          this.clearAccessSession();
        },
      })
    );
  }

  private stopAccessTimer(): void {
    this.accessTimerSubscription?.unsubscribe();
    this.accessTimerSubscription = undefined;
    this.accessExpiresAt = 0;
    this.secondsRemaining = 0;
  }

  private resetProtectedIdentityFilter(): void {
    const control = this.filterComponent?.form?.get('protectedIdentityStatus');
    if (!control) return;

    if (this.toProtectedIdentityFilterValue(control.value) !== null) {
      this.itemsSource = [];
      this.totalResults = 0;
    }

    control.setValue('');
    control.markAsDirty();
    control.updateValueAndValidity({ emitEvent: false });
    this.cdr.detectChanges();
  }

  private toProtectedIdentityFilterValue(value: unknown): boolean | null {
    if (value === true || value === 'true') return true;
    if (value === false || value === 'false') return false;
    return null;
  }

  private removeProtectedIdentityFilter(): void {
    const filterComponent = this.filterComponent;
    const hadActiveFilter = filterComponent?.form?.contains('protectedIdentityStatus') === true;

    filterComponent?.removeFilter('protectedIdentityStatus');
    this.filterDefinition = this.filterDefinition?.filter(item => item.name !== 'protectedIdentityStatus');
    this.defaultFilters = this.defaultFilters?.filter(name => name !== 'protectedIdentityStatus');
    this.stopAccessTimer();
    this.hasProtectedIdentityAccessSession = false;

    if (!hadActiveFilter) return;

    super.onFilterChange({
      ...filterComponent.form.value,
      protectedIdentityStatus: null,
      selectedFields: filterComponent.filters
        .filter(item => item.selected && item.name !== 'protectedIdentityStatus')
        .map(item => item.name),
    });
  }

  closeAmortizationPlanSidebar(): void {
    this._sidebarService.toggleSidebar(false);
  }

  onAmortizationPlanCreated(): void {
    this.closeAmortizationPlanSidebar();
    this.reloadCurrentList();
  }

  private openCreateAmortizationPlanSidebar(invoiceIds: string[]): void {
    this.amortizationPlanInvoiceIds = (invoiceIds ?? []).filter(id => !!id);
    this._sidebarService.setSidebarContent({
      template: this.createAmortizationPlanSidebar,
      onClose: () => {
        this.amortizationPlanInvoiceIds = [];
      },
    });
    this._sidebarService.toggleSidebar(true);
  }

  private reloadCurrentList(): void {
    const selectedFields =
      this.filterComponent?.filters?.filter(filter => filter.selected).map(filter => filter.name) ?? this.filterState?.selectedFields;
    const filter = {
      ...(this.filterComponent?.form?.value ?? this.filterState ?? this.getPagingChangedFallbackFilter()),
      selectedFields: selectedFields ?? this.defaultFilters ?? [],
    };

    (this as any).sendPageFilter(filter);
  }
}
